/**
 * PMC — relative momentum cycle. Ported from the MPP C++ `AfiatEquiScan_PMC_Function`
 * (no chart study → from the file).
 *
 *   DiffROC = 20-bar ROC% of close − 20-bar ROC% of reference SPY
 *   PMC     = EMA of DiffROC, smoothing 2/8   (the "line")
 *
 * DELIBERATE DEVIATION from the C++: the source rounds each ROC to a whole
 * percent before subtracting, quantizing DiffROC to integers (and ±1-stepping
 * the histogram). We keep full resolution — sub-percent relative momentum
 * registers, and quadrant/cross-age flips land on the true crossing bar
 * rather than a whole-percent boundary. Expect small state/age differences
 * vs the MPP scanner on bars where the diff sits near a boundary.
 *
 * The C++ returns a 1–4 QUADRANT — the source-of-truth state, each a distinct
 * color (the user's "PMC has 4 colors"):
 *   1: DiffROC ≥ 0 AND DiffROC ≥ PMC   (bull, accelerating)
 *   2: DiffROC ≥ 0 AND DiffROC <  PMC  (bull, fading)
 *   3: DiffROC < 0 AND DiffROC ≥ PMC   (bear, recovering)
 *   4: DiffROC < 0 AND DiffROC <  PMC  (bear, accelerating)
 * Separately, a signed bars-since-cross COUNT (DiffROC crossing its PMC line)
 * is the "age" readout, exposed as its own column.
 *
 * Faithful detail: the C++ seed branch is dead code (the loop starts 20 bars in),
 * so the EMA effectively seeds at 0 (prev = 0). We replicate that — it only biases
 * the deep-past warmup; the EMA has long converged by the latest bar.
 * `needsComparative` → SPY supplied by the scanner / chart.
 *
 * The Person Screener's PMC triple (mpp/person.ts → scanner_panel.jp_pmc /
 * jp_pmc_ma / jp_pmc_stage) wraps THIS module too: value = latest DiffROC,
 * MA = the latest PMC line, stage = the latest quadrant — so the Person view,
 * the chart study, and the PMC Trend column all read one implementation.
 */
import type { OHLCV, IndicatorParams, SharedParam, SharedIndicatorDef } from "../types";
import { familyGroup } from "../indicatorGroups";
import { TF_DW, TF_PARAM } from "./timeframe";
import { resampleByLastClose, lastClosedHtIndexPerDay, htIndexPerDay, htfWalkForward, type HtFreq } from "./weekly";

const ROC = 20; // default relative-ROC lookback
const SMOOTH = 8; // default PMC EMA smoothing (α = 2 / SMOOTH)

const len = (v: number | undefined, d: number) =>
  Math.max(1, Math.round(typeof v === "number" && Number.isFinite(v) ? v : d));

/** Configurable PMC inputs. The DiffROC lookback drives the value/histogram;
 *  the smoothing length drives the PMC line / quadrant / cross-age. Defaults
 *  20 / 8 reproduce the canonical PMC (α = 2/8). */
export const PMC_PARAMS: ReadonlyArray<SharedParam> = [
  { id: "roc", label: "ROC length", kind: "number", default: ROC, min: 2, max: 200, step: 1 },
  { id: "smooth", label: "Smoothing", kind: "number", default: SMOOTH, min: 1, max: 100, step: 1 },
];
/** The VALUE / histogram (raw DiffROC) depends only on the ROC lookback — no
 *  smoothing input. */
export const PMC_VALUE_PARAMS: ReadonlyArray<SharedParam> = [PMC_PARAMS[0]!];

export type PmcParts = {
  diff: (number | null)[]; // DiffROC — the histogram
  line: (number | null)[]; // PMC — EMA(DiffROC, 2/8), the line
  quad: (number | null)[]; // 1..4 quadrant (the 4-color state)
  count: (number | null)[]; // signed bars-since-cross (+ above line / − below); 0 pre-cross
};

/** 20-bar relative ROC difference, full resolution (the C++ rounded each ROC
 *  to a whole percent — see header). Null for the first ROC bars. */
function diffRoc(closes: ReadonlyArray<number>, ref: ReadonlyArray<number>, roc = ROC): (number | null)[] {
  const n = closes.length;
  const out: (number | null)[] = new Array(n).fill(null);
  for (let i = roc; i < n; i++) {
    const c0 = closes[i]!, c20 = closes[i - roc]!, r0 = ref[i]!, r20 = ref[i - roc]!;
    if (!(c20 > 0) || !(r20 > 0) || !Number.isFinite(c0) || !Number.isFinite(r0)) continue;
    out[i] = (c0 / c20 - 1) * 100 - (r0 / r20 - 1) * 100;
  }
  return out;
}

/** From DiffROC, derive the PMC line, the 1–4 quadrant, and the signed cross-count.
 *  EMA seeded at 0 (faithful to the C++ dead seed branch). */
function pmcParts(diff: (number | null)[], smooth = SMOOTH): Omit<PmcParts, "diff"> {
  const n = diff.length;
  const ALPHA = 2 / smooth;
  const line: (number | null)[] = new Array(n).fill(null);
  const quad: (number | null)[] = new Array(n).fill(null);
  const count: (number | null)[] = new Array(n).fill(null);
  let i0 = 0;
  while (i0 < n && (diff[i0] == null || !Number.isFinite(diff[i0]!))) i0++;
  if (i0 >= n) return { line, quad, count };
  let prevDiff = 0, prevPmc = 0, buy = 0, sell = 0;
  for (let i = i0; i < n; i++) {
    const d = diff[i];
    if (d == null || !Number.isFinite(d)) {
      line[i] = i > 0 ? line[i - 1] ?? null : null;
      quad[i] = i > 0 ? quad[i - 1] ?? null : null;
      count[i] = i > 0 ? count[i - 1] ?? null : null;
      continue;
    }
    const pmc = ALPHA * (d - prevPmc) + prevPmc;
    if (d >= pmc && prevDiff < prevPmc) { buy += 1; sell = 0; }
    else if (d < pmc && prevDiff >= prevPmc) { sell += 1; buy = 0; }
    else { if (buy !== 0) buy += 1; if (sell !== 0) sell += 1; }
    line[i] = pmc;
    count[i] = buy > 0 ? buy : sell > 0 ? -sell : 0;
    quad[i] = d >= 0 ? (d >= pmc ? 1 : 2) : (d >= pmc ? 3 : 4);
    prevDiff = d;
    prevPmc = pmc;
  }
  return { line, quad, count };
}

const EMPTY = (n: number): PmcParts => ({
  diff: new Array(n).fill(null), line: new Array(n).fill(null),
  quad: new Array(n).fill(null), count: new Array(n).fill(null),
});

export function pmcDailyParts(data: OHLCV, p: IndicatorParams = {}): PmcParts {
  const { closes, compareCloses } = data;
  const n = closes.length;
  if (!compareCloses || compareCloses.length !== n) return EMPTY(n);
  const roc = len(p.roc, ROC), smooth = len(p.smooth, SMOOTH);
  const diff = diffRoc(closes, compareCloses, roc);
  return { diff, ...pmcParts(diff, smooth) };
}

// `inclusive` maps each day to the week it BELONGS to (counts the in-progress
// week) — used by the scanner so the latest row reflects the current weekly bar.
// Default (false) is lookahead-off (last completed week).
//
// NOTE: this maps each daily bar onto the FULL-series weekly value, so a
// historical mid-week bar reads its week's eventual value — it REPAINTS. It's
// fine for the SCANNER (which only reads the latest element — never history),
// but charts must NOT use it for the series. Use `pmcWeeklyNoRepaint` to render
// weekly PMC across daily bars.
export function pmcWeeklyParts(data: OHLCV, inclusive = false, p: IndicatorParams = {}): PmcParts {
  const { closes, compareCloses, times } = data;
  const n = closes.length;
  if (!compareCloses || compareCloses.length !== n || !times || times.length !== n) return EMPTY(n);
  const roc = len(p.roc, ROC), smooth = len(p.smooth, SMOOTH);
  const sym = resampleByLastClose(times, closes, "week");
  const ref = resampleByLastClose(times, compareCloses, "week");
  const m = Math.min(sym.htCloses.length, ref.htCloses.length);
  if (m < roc + 2) return EMPTY(n);
  const wdiff = diffRoc(sym.htCloses.slice(0, m), ref.htCloses.slice(0, m), roc);
  const wp = pmcParts(wdiff, smooth);
  const ht = sym.htTimes.slice(0, m);
  const idxPerDay = inclusive ? htIndexPerDay(times, ht) : lastClosedHtIndexPerDay(times, ht);
  const pick = (arr: (number | null)[]) =>
    times.map((_, i) => { const k = idxPerDay[i]!; return k >= 0 && k < m ? arr[k]! : null; });
  return { diff: pick(wdiff), line: pick(wp.line), quad: pick(wp.quad), count: pick(wp.count) };
}

/**
 * Higher-timeframe PMC for CHART rendering — NO REPAINT (walk-forward; see
 * `htfWalkForward`). For each chart bar, DiffROC + PMC line + quadrant +
 * cross-count are recomputed on the bucket closes (and SPY bucket closes)
 * VISIBLE AS OF that bar, so a past bar's PMC can't change when later bars
 * arrive. `freq="week"` across daily bars is the classic weekly study; the
 * latest bar equals `pmcWeeklyParts(data, true)`'s last element (the live
 * current week), so chart and scanner agree at the live edge. `freq="day"`
 * across INTRADAY bars is true daily PMC on an intraday chart — history
 * frozen per session, the latest bar reading the live current session.
 */
export function pmcHtfNoRepaint(data: OHLCV, freq: HtFreq = "week"): PmcParts {
  const { closes, compareCloses, times, opens, highs, lows, volumes } = data;
  const n = closes.length;
  if (!compareCloses || compareCloses.length !== n || !times || times.length !== n) return EMPTY(n);
  const cl = closes as number[];
  const zero = new Array<number>(n).fill(0);
  const res = htfWalkForward(
    times,
    (opens as number[]) ?? cl, // o/h/l unused by PMC (it resamples by last close)
    (highs as number[]) ?? cl,
    (lows as number[]) ?? cl,
    cl,
    (volumes as number[]) ?? zero,
    freq,
    (s) => {
      const cc = s.c;
      const ref = s.cmp;
      // ROC + 1 closes = exactly when diffRoc yields its first value — the
      // same warmup as the direct computation, so the projection agrees with
      // pmcDailyParts from the very first defined bar (was +2: one bucket late).
      if (!ref || cc.length < ROC + 1) return null;
      const d = diffRoc(cc, ref);
      const p = pmcParts(d);
      const k = cc.length - 1;
      return { diff: d[k] ?? null, line: p.line[k] ?? null, quad: p.quad[k] ?? null, count: p.count[k] ?? null };
    },
    { compareCloses },
  );
  const diff: (number | null)[] = new Array(n).fill(null);
  const line: (number | null)[] = new Array(n).fill(null);
  const quad: (number | null)[] = new Array(n).fill(null);
  const count: (number | null)[] = new Array(n).fill(null);
  for (let i = 0; i < n; i++) {
    const r = res[i];
    if (!r) continue;
    diff[i] = r.diff;
    line[i] = r.line;
    quad[i] = r.quad;
    count[i] = r.count;
  }
  return { diff, line, quad, count };
}

/** Back-compat name — weekly projection across daily bars. */
export const pmcWeeklyNoRepaint = (data: OHLCV): PmcParts => pmcHtfNoRepaint(data, "week");

/** Bars-since-cross magnitude (the age readout); null pre-cross. */
const ageOf = (count: (number | null)[]): (number | null)[] =>
  count.map((v) => (v == null || v === 0 ? null : Math.abs(v)));

// Quadrant labels: states 1,2 are bull (DiffROC ≥ 0), 3,4 are bear.
const LABELS = { pos: "Bull", neg: "Bear", zero: "—" } as const;

// Group comes from the shared registry (one source of truth for chart + scanner
// — see lib/indicators/shared/indicatorGroups.ts). Per timeframe the column
// order is Trend · Age · Value · MA.
const PMC_GROUP = familyGroup("pmc");

// One def per concept + a Timeframe select (default Daily). PMC has no baked
// columns or criteria, so both timeframes recompute via /api/scanner/indicator
// (the tf param flows through computeSeries) — unchanged from the old per-tf
// defs. The historical "-daily" ids are reused as canonical; only the weekly ids
// alias. Daily = pmcDailyParts, Weekly = pmcWeeklyParts(d, true, p).
export const PMC_DAILY: SharedIndicatorDef = {
  id: "mpp-pmc-daily", label: "Afiat - Relative Momentum", group: PMC_GROUP,
  kind: "state", format: "quad", stateLabels: LABELS, needsComparative: true,
  params: [TF_DW, ...PMC_PARAMS],
  computeSeries: (d, p) => (p[TF_PARAM] === 1 ? pmcWeeklyParts(d, true, p) : pmcDailyParts(d, p)).quad,
};
export const PMC_DAILY_AGE: SharedIndicatorDef = {
  id: "mpp-pmc-daily-age", label: "Afiat - Relative Momentum Age", group: PMC_GROUP,
  kind: "age", format: "age", needsComparative: true,
  params: [TF_DW, ...PMC_PARAMS],
  computeSeries: (d, p) => ageOf((p[TF_PARAM] === 1 ? pmcWeeklyParts(d, true, p) : pmcDailyParts(d, p)).count),
};
/** Raw DiffROC — the PMC value itself (signed, full resolution). */
export const PMC_DAILY_VALUE: SharedIndicatorDef = {
  id: "mpp-pmc-daily-value", label: "Afiat - Relative Momentum", group: PMC_GROUP,
  format: "signed", needsComparative: true,
  params: [TF_DW, ...PMC_VALUE_PARAMS],
  computeSeries: (d, p) => (p[TF_PARAM] === 1 ? pmcWeeklyParts(d, true, p) : pmcDailyParts(d, p)).diff,
};
/** Raw PMC line — EMA(DiffROC, 2/8), the MA of the PMC (signed). */
export const PMC_DAILY_MA: SharedIndicatorDef = {
  id: "mpp-pmc-daily-ma", label: "Afiat - Relative Momentum", group: PMC_GROUP,
  format: "signed", needsComparative: true,
  params: [TF_DW, ...PMC_PARAMS],
  computeSeries: (d, p) => (p[TF_PARAM] === 1 ? pmcWeeklyParts(d, true, p) : pmcDailyParts(d, p)).line,
};

export const PMC_INDICATORS: ReadonlyArray<SharedIndicatorDef> = [
  PMC_DAILY, PMC_DAILY_AGE, PMC_DAILY_VALUE, PMC_DAILY_MA,
];
